import type { MembershipQuestionTypeRow, ProfileRankingRow } from '../membership/membership-application.types';

/**
 * Steps that show “Step X of N” — profile 1–15 + membership questions/ranking/review only.
 * Excludes `/welcome/start`, profile thank-you, application intro, and application-complete shells.
 */
export const ONBOARDING_SCRIPT_INPUT_STEPS_TOTAL = 18;

/**
 * Verbatim copy from the Movemeback onboarding script (counted steps from “About you” + welcome/start).
 * Edit here only — templates should bind to this or `profileOnboardingSteps` below.
 */
export const ONBOARDING_COPY = {
  register: {
    title: 'Join a community building Africa’s future',
    subtitle: 'Connect with top African talent, opportunities, and ideas shaping the continent.',
    submitCta: 'Join Movemeback',
  },
  /** `/welcome/start` — not counted in “Step X of N”. */
  welcomeStart: {
    title: 'Welcome to Movemeback',
    lead:
      'Movemeback is a curated community connecting Africans and the diaspora to opportunities, networks, and ideas shaping the continent.',
    processIntro: 'Membership requires a short application process:',
    bullets: [
      'Complete your profile so we understand your background',
      'Share your preferences so we can serve you better',
      'Answer a few questions so we can assess your fit for the community',
    ],
    timeNote: 'This typically takes 10–15 minutes and can be completed in stages.',
    cta: 'Get started',
  },
  emailVerification: {
    title: 'Verify your email',
    sentIntro: 'We’ve sent a confirmation link to:',
    afterEmail: 'Click the link in your email to continue.',
    spamHint: 'If it hasn’t arrived, check your spam folder or wait a few minutes.',
    verifiedButton: 'I’ve verified my email',
    resend: 'Resend email',
    changeEmail: 'Change email',
    resentSuccess: 'Email resent successfully!',
  },
  phoneVerification: {
    title: 'Verify your phone number',
    sentIntro: 'We’ve sent a 6-digit code to:',
    enterBelow: 'Enter it below to continue.',
    enterCodeHeading: 'Enter verification code',
    verifyButton: 'Verify code',
    resend: 'Resend code',
    changeNumber: 'Change number',
  },
  /** Step 19 — profile complete before membership application (`activeStep === 16`). */
  profileCompleteTransition: {
    title: 'Your profile is complete — continue your application',
    p1: 'We now have a strong understanding of your background.',
    p2: 'Next, we’ll ask a few questions to better understand your thinking, values, and ambitions.',
    p3: 'This helps us maintain a high-quality and trusted community.',
    continueCta: 'Continue',
  },
  membership: {
    loading: 'Loading your application…',
    applicationStart: {
      title: 'Membership application',
      lead: 'You’re almost there.',
      introP1:
        'Movemeback is a curated community of high-quality African talent, builders, and leaders.',
      introP2:
        'We review every application to maintain a high-quality, trusted network — ensuring every member both contributes and benefits.',
      questionsIntro: 'You’ll answer a few final questions to help us understand:',
      questionsBullets: [
        'Your mindset and ambitions',
        'Your experiences and achievements',
        'How you want to engage with Africa and the community',
      ],
      communityLine: 'This helps us ensure a strong, aligned community.',
      timeP1: 'This will take 3–5 minutes.',
      timeP2: 'You’ll hear back within a few days once your application has been reviewed.',
      reinforcement: 'We’re looking for individuals who are building and contributing to Africa’s future.',
      startButton: 'Start application',
    },
    applicationQuestions: {
      title: 'Tell us more about you',
      intro:
        'Share thoughtful responses to help us understand your mindset, experiences, and ambitions.',
      minCharsNotePrefix: 'Minimum ',
      minCharsNoteSuffix: ' characters per answer.',
      validationHintPrefix: 'Please enter at least ',
      validationHintSuffix: ' characters (leading and trailing spaces are not counted).',
    },
    applicationRanking: {
      title: 'Self-assessment',
      body: 'Rate how strongly each area reflects you today.',
      chooseLevel: 'Choose a level',
      chooseLevelError: 'Please choose a level',
      optionalNotePlaceholder: 'Supporting explanation',
      /** Expands inline help for a dimension (uses `field_help_text` when provided). */
      helpToggle: 'Help',
    },
    applicationReview: {
      title: 'Review your application',
      yourAnswers: 'Your application answers',
      yourSelfAssessment: 'Your self-assessment',
      submitButton: 'Submit application',
    },
    applicationComplete: {
      title: 'Thank you — your application has been submitted',
      body:
        'We’re now reviewing your application to ensure a high-quality and trusted community. We’ll be in touch once your membership has been confirmed.',
      reinforcement:
        'We carefully review each application to maintain the quality, relevance, and integrity of the network.',
      continueButton: 'Finish',
    },
    shared: {
      back: 'Back',
      continue: 'Continue',
      saveDraft: 'Save draft',
    },
  },
  membershipPending: {
    stepLabel: 'Step 18 of 18',
    /** First-time applicants awaiting decision (cohort 138+). */
    newCohort: {
      title: 'Your application is under review',
      p1: 'We’re carefully reviewing your application to maintain a high-quality community across Movemeback.',
      p2Before: 'This process typically takes ',
      p2Emphasis: '5–7 days',
      p2After: '.',
      p3: 'We’ll notify you as soon as your membership is confirmed.',
      p4: 'Movemeback is a curated network. Every member is reviewed before gaining access.',
    },
    /** Existing members / periodic review (after final counted step). */
    existingUser: {
      title: 'Your membership is under review',
      p1: 'As part of maintaining a high-quality community, we periodically review member profiles.',
      p2: 'Your account is currently under review.',
      p3: 'Please ensure your information is up to date while we complete this process.',
      p4: 'You’ll gain full access once your membership is confirmed.',
    },
    updateApplication: 'Update your application',
    updateProfile: 'Update your profile',
    contactTeam: 'Contact the team',
  },
} as const;

/**
 * Profile onboarding: `activeStep` 1–15 map to steps 1–15 of {@link ONBOARDING_SCRIPT_INPUT_STEPS_TOTAL}; thank-you (16) has no badge.
 * Index = activeStep - 1.
 */
export const PROFILE_ONBOARDING_STEPS: ReadonlyArray<{ title: string; description: string }> = [
  {
    title: 'Tell us about you',
    description: 'This helps us understand who you are and personalise your experience.',
  },
  {
    title: 'What best describes you?',
    description: 'Select the option that best reflects your current situation.',
  },
  {
    title: 'Share your professional profile',
    description: 'Add your LinkedIn profile or upload your CV so we can understand your experience.',
  },
  {
    title: 'Your professional profile',
    description: 'Tell us your title, industry, and level of experience so we can better position you.',
  },
  {
    title: 'What are your strengths?',
    description: 'Select the strengths that best reflect how you work and what you bring.',
  },
  {
    title: 'What interests you about Africa?',
    description: 'Help us understand what draws you to the continent.',
  },
  {
    title: 'What opportunities are you looking for?',
    description: 'Select the types of opportunities you’re interested in.',
  },
  {
    title: 'What support do you need?',
    description: 'Tell us how the community can support you.',
  },
  {
    title: 'Your global connections',
    description: 'Add countries you’re connected to and your level of experience with each.',
  },
  {
    title: 'Your current roles',
    description: 'Select the roles that best describe what you do today.',
  },
  {
    title: 'Which industries do you care about?',
    description: 'Select the industries you’re most interested in or active in.',
  },
  {
    title: 'What impact matters most to you?',
    description: 'Select the areas of transformation you care about across the continent.',
  },
  {
    title: 'Which global goals matter to you?',
    description: 'Choose the goals that align with your values and interests.',
  },
  {
    title: 'Where do you want to work or engage?',
    description: 'Indicate your interest across different organisation types.',
  },
  {
    title: 'What are your earning expectations?',
    description: 'Set your expected annual income range to guide opportunity matching.',
  },
];

/**
 * Plan step 21 — six prompts in script order. Map each to `MEMBERSHIP_QUESTION_LABEL_BY_ID[apiId]`
 * using ids from `GET …/type/membership/question/`.
 */
export const MEMBERSHIP_QUESTION_PLAN_TEXTS_IN_ORDER: readonly string[] = [
  'What is your life goal / north star?',
  'What achievement are you most proud of?',
  'Describe your biggest failure and what you learnt',
  'What policy or change would you introduce?',
  'How do you embody Movemeback values?',
  'Why do you want to join and contribute?',
];

/**
 * Override labels for membership application long-form questions, keyed by API question `id`.
 */
export const MEMBERSHIP_QUESTION_LABEL_BY_ID: Record<number, string> = {};

export const MEMBERSHIP_RANKING_DIMENSIONS: ReadonlyArray<{
  field: keyof ProfileRankingRow;
  note: keyof ProfileRankingRow;
  label: string;
}> = [
  { field: 'professional_user', note: 'professional_user_note', label: 'Professional' },
  { field: 'skilled_user', note: 'skilled_user_note', label: 'Skilled' },
  { field: 'creative_user', note: 'creative_user_note', label: 'Creative' },
  { field: 'education_user', note: 'education_user_note', label: 'Education' },
  { field: 'entrepreneurship_user', note: 'entrepreneurship_user_note', label: 'Entrepreneurship' },
  { field: 'community_user', note: 'community_user_note', label: 'Community' },
];

export function membershipQuestionLabel(q: MembershipQuestionTypeRow): string {
  const id = q.id ?? q.value;
  if (id != null) {
    const override = MEMBERSHIP_QUESTION_LABEL_BY_ID[id];
    if (override != null && String(override).trim() !== '') {
      return override;
    }
  }
  return (q.question ?? q.name ?? '').trim();
}
